import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Paper, Chip, CircularProgress, Alert, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { tokens } from '../../../../theme';
import v2Api from '../../../../utils/v2Api';
import getCookies from '../../../../utils/getCookies';
import { TestSessionProvider, useTestSession } from './TestSessionContext';
import QuestionUIRenderer from './renderers/QuestionUIRenderer';
import PdfRenderer from './renderers/PdfRenderer';

// Review Mode — teachers (assigned to the course) and admins see EXACTLY what
// students see, with the correct answers filled in and ZERO student data:
// it literally reuses the test-taking renderers inside a preview session
// (session.id = null → save/submit/localStorage are all no-ops).
//   Question-UI test -> QuestionUIRenderer, correct answers pre-selected
//                       (FRQs stay blank — there is no single correct text)
//   PDF test         -> PdfRenderer with file_url SWAPPED to the watermarked
//                       teacher PDF (the original never reaches this page),
//                       answer sheet pre-filled with the key
//   PDF, no watermark-> notice + a compact answer key (nothing to render)

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// Same crisp-PDF pattern as the test PDF displays (StudentResultV2/PdfRenderer)
const pixelRatio = Math.max(window.devicePixelRatio || 1, 2);
const PdfDoc = ({ file }) => {
    const [pages, setPages] = React.useState(null);
    return (
        <Document file={file} onLoadSuccess={({ numPages }) => setPages(numPages)}
            loading={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>}
        >
            {pages && Array.from({ length: pages }, (_, i) => (
                <Page key={i + 1} pageNumber={i + 1} scale={pixelRatio} renderTextLayer={false} renderAnnotationLayer={false} />
            ))}
        </Document>
    );
};

// What a correct answer looks like in answer_text terms (QuestionSlot formats:
// mcq = option label, multi-select = comma-joined labels, text types = the
// primary accepted answer). FRQ types have no single correct text — blank.
const correctAnswerText = (q) => {
    if (['saq', 'dbq', 'leq', 'frq'].includes(q.question_type)) return '';
    if (q.question_type === 'mcq') return q.answer || '';
    const acc = Array.isArray(q.accepted_answers) ? q.accepted_answers : [];
    return q.answer || acc[0] || '';
};

const allQuestions = (test) =>
    (test?.v2_test_sections || []).flatMap((s) => s.v2_test_questions || []);

const ReviewInner = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { id: courseUuid, courseTestId } = useParams();
    const navigate = useNavigate();
    const { hydrate, test } = useTestSession();

    const currentUser = getCookies('_auth_state');
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reviewTest, setReviewTest] = useState(null); // unswapped — keeps watermarked/key urls for the header buttons
    // Explanations overlay (teacher/admin-only PDF, esp. FRQ explanations).
    // Kept MOUNTED while hidden so its scroll position survives toggling.
    // Width is drag-adjustable via the left-edge handle (remembered per browser).
    const [explOpen, setExplOpen] = useState(true);
    const [explWidth, setExplWidth] = useState(() => {
        const saved = parseFloat(localStorage.getItem('v2ReviewExplWidth'));
        return saved >= 20 && saved <= 90 ? saved : 46;
    });
    const [explDragging, setExplDragging] = useState(false);
    const bodyRef = React.useRef(null);
    const startExplDrag = (e) => {
        e.preventDefault();
        const rect = bodyRef.current?.getBoundingClientRect();
        if (!rect) return;
        setExplDragging(true);
        const onMove = (ev) => {
            const pct = ((rect.right - ev.clientX) / rect.width) * 100;
            setExplWidth(Math.min(90, Math.max(20, pct)));
        };
        const onUp = () => {
            setExplDragging(false);
            setExplWidth((w) => { localStorage.setItem('v2ReviewExplWidth', String(w)); return w; });
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    useEffect(() => {
        if (userRole === 'student') {
            navigate(`/classrooms/${courseUuid}/tests`, { replace: true });
            return;
        }
        v2Api.get(`/course-tests/${courseTestId}/review`)
            .then((res) => {
                const t = res.data.data.test;
                setReviewTest(t);
                // PDF tests: the renderer reads test.file_url — hand it the
                // watermarked teacher version instead (never the original).
                const testForRenderer = t.use_question_ui ? t : { ...t, file_url: t.watermarked_file_url || null };
                hydrate({
                    session: { id: null, status: 'review' },
                    test: testForRenderer,
                    course_test: res.data.data,
                    saved_answers: allQuestions(t)
                        .map((q) => ({ question_id: q.id, answer_text: correctAnswerText(q), flagged: false }))
                        .filter((a) => a.answer_text !== ''),
                });
            })
            .catch((err) => setError(err.response?.data?.message || 'Failed to load review'))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseTestId]);

    // The renderers expect the session layout: fixed height, no outer scroll
    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'v2-test-review-no-outer-scroll';
        style.textContent = 'main.content { overflow: hidden !important; }';
        document.head.appendChild(style);
        return () => style.remove();
    }, []);

    if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;
    if (error) return <Box p={3}><Alert severity="error">{error}</Alert></Box>;
    if (!reviewTest) return null;

    const pdfWithoutWatermark = !reviewTest.use_question_ui && !reviewTest.watermarked_file_url;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', mx: 2 }}>
            {/* Header */}
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: colors.grey[700], display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(`/classrooms/${courseUuid}/tests`)}>
                    Back
                </Button>
                <Typography variant="h6" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 160 }}>
                    {reviewTest.title}
                </Typography>
                {/* NO answer-key link here: the key file is AI-grading input only —
                    the add/edit page is its sole legal UI surface (admin spec) */}
                {reviewTest.explanations_file_url && (
                    <Button size="small" variant={explOpen ? 'contained' : 'outlined'}
                        onClick={() => setExplOpen((p) => !p)}>
                        {explOpen ? 'Hide Explanations' : 'Show Explanations'}
                    </Button>
                )}
                <Chip label="Review Mode — correct answers filled in, no student data" variant="outlined" size="small"
                    sx={{ color: 'text.primary', borderColor: 'text.secondary', fontWeight: 600 }} />
            </Box>

            {/* Body: the REAL taking renderers (or no-watermark fallback), with the
                explanations OVERLAY sliding over the right (answer) side — an
                extra column would crush the already-dense review layout */}
            <Box ref={bodyRef} sx={{ flex: 1, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
            {pdfWithoutWatermark ? (
                <Box sx={{ overflowY: 'auto', py: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        No teacher (watermarked) version of this test has been uploaded — showing the answer key only.
                    </Alert>
                    {(reviewTest.v2_test_sections || []).map((section) => (
                        <Paper key={section.id} sx={{ p: 2, mb: 2, bgcolor: colors.primary[600] }}>
                            <Typography variant="h6" mb={1}>
                                {section.section_name || `Section ${section.section_number}`}
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {(section.v2_test_questions || []).map((q) => {
                                    const ans = correctAnswerText(q) || (['saq', 'dbq', 'leq', 'frq'].includes(q.question_type) ? 'FRQ' : '—');
                                    return (
                                        <Box key={q.id} sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.25,
                                            borderRadius: 1, bgcolor: colors.primary[700], border: '1px solid', borderColor: 'divider',
                                        }}>
                                            <Typography variant="caption" fontWeight={700}>Q{q.question_number}</Typography>
                                            <Typography variant="caption" fontWeight={700} sx={{ color: '#51BE90' }}>{ans}</Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Paper>
                    ))}
                </Box>
            ) : (
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {test?.use_question_ui ? <QuestionUIRenderer /> : <PdfRenderer />}
                </Box>
            )}

            {/* Explanations overlay — slides over the answer side; kept MOUNTED
                while hidden so the PDF's scroll position survives toggling */}
            {reviewTest.explanations_file_url && (
                <Box sx={{
                    position: 'absolute', top: 0, right: 0, bottom: 0,
                    width: `${explWidth}%`,
                    bgcolor: 'background.paper',
                    borderLeft: '1px solid', borderColor: 'divider',
                    boxShadow: 8, zIndex: 20,
                    display: 'flex',
                    transform: explOpen ? 'translateX(0)' : 'translateX(110%)',
                    transition: explDragging ? 'none' : 'transform .25s ease',
                }}>
                    {/* drag handle — resize the overlay */}
                    <Box onMouseDown={startExplDrag} sx={{
                        width: 8, flexShrink: 0, cursor: 'col-resize',
                        bgcolor: 'divider', '&:hover': { bgcolor: colors.blueAccent[500] },
                    }} />
                    <Box sx={{
                        flex: 1, minWidth: 0, overflow: 'auto', p: 1,
                        '& canvas': { width: '100% !important', height: 'auto !important' },
                    }}>
                        <PdfDoc file={reviewTest.explanations_file_url} />
                    </Box>
                </Box>
            )}
            </Box>
        </Box>
    );
};

// Wrapper that provides the (preview-mode) session context — readOnly makes
// every answer mutator a no-op, so the pre-filled keys can't be changed.
const TestReviewV2 = () => (
    <TestSessionProvider readOnly>
        <ReviewInner />
    </TestSessionProvider>
);

export default TestReviewV2;
